import React, { useState, useCallback, useEffect } from "react";
import {
  Instagram, Facebook, Calendar, Link as LinkIcon, BarChart3, Loader2, Edit3,
  Check, CheckCircle2, X, Trash2, RefreshCw, Send, ExternalLink, Activity, Rocket,
  Clock, Video,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DoozyAd card components — extracted from DoozyAdTab.js (iter151 refactor).
 * Behaviour preserved exactly, all data-testids kept for E2E parity.
 *
 * Exports: EmptyState, DraftCard, ScheduledCard, PublishedCard, BoostPanel, Stat
 */

export const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-10 text-center">
    <Icon size={28} className="text-gray-300 mx-auto mb-2" />
    <p className="text-sm font-semibold text-[#1E1B4B]">{title}</p>
    <p className="text-xs text-[#64748B] mt-1">{subtitle}</p>
  </div>
);

export const DraftCard = ({ draft, authAxios, onChange, canPublish }) => {
  const [selectedCaption, setSelectedCaption] = useState(draft.captions?.[0] || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pubIG, setPubIG] = useState(true);
  const [pubFB, setPubFB] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  const approve = async () => {
    if (!selectedCaption.trim()) return;
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/drafts/${draft.draft_id}/approve`, {
        selected_caption: selectedCaption,
        publish_to_instagram: pubIG,
        publish_to_facebook: pubFB,
      });
      await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Approve failed");
    }
    setBusy(false);
  };

  const schedule = async () => {
    if (!selectedCaption.trim() || !scheduleAt) return;
    // Convert datetime-local (treated as local) to UTC ISO
    const localDt = new Date(scheduleAt);
    if (Number.isNaN(localDt.getTime())) {
      alert("Invalid date/time");
      return;
    }
    if (localDt.getTime() <= Date.now()) {
      alert("Schedule time must be in the future");
      return;
    }
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/drafts/${draft.draft_id}/schedule`, {
        selected_caption: selectedCaption,
        scheduled_for: localDt.toISOString(),
        publish_to_instagram: pubIG,
        publish_to_facebook: pubFB,
      });
      setShowSchedule(false);
      setScheduleAt("");
      await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Schedule failed");
    }
    setBusy(false);
  };

  const skip = async () => {
    if (!window.confirm("Skip this draft? It will be archived without publishing.")) return;
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/drafts/${draft.draft_id}/skip`);
      await onChange();
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col sm:flex-row" data-testid={`doozyad-draft-${draft.draft_id}`}>
      <div className="sm:w-64 shrink-0 bg-gray-100 aspect-square sm:aspect-auto relative">
        {draft.media_type === "video" ? (
          <video src={draft.public_image_url} className="w-full h-full object-cover" controls preload="metadata" />
        ) : (
          <img src={draft.public_image_url} alt={draft.theme_name} className="w-full h-full object-cover" loading="lazy" />
        )}
        {draft.source === "uploaded" && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/90 text-white text-[10px] font-semibold rounded-full uppercase tracking-wide">
            Uploaded
          </span>
        )}
        {draft.source === "sora2" && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-violet-500/90 text-white text-[10px] font-semibold rounded-full uppercase tracking-wide flex items-center gap-1">
            <Video size={9} /> Sora 2
          </span>
        )}
      </div>
      <div className="flex-1 p-5 flex flex-col gap-3">
        <div>
          <div className="text-xs text-[#DC283C] font-semibold uppercase tracking-wider">{draft.theme_name}</div>
          <div className="text-xs text-[#64748B] mt-0.5">Generated {new Date(draft.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#64748B] block mb-1">Pick a caption (or edit the chosen one)</label>
          <div className="space-y-1.5">
            {(draft.captions || []).map((c, i) => (
              <label key={`${draft.draft_id}-cap-${i}-${c.slice(0, 12)}`} className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs leading-snug ${selectedCaption === c ? "border-[#DC283C] bg-pink-50" : "border-gray-200 hover:border-gray-300"}`}>
                <input type="radio" name={`cap-${draft.draft_id}`} checked={selectedCaption === c}
                  onChange={() => { setSelectedCaption(c); setEditing(false); }} className="mt-0.5 accent-[#DC283C]" />
                <span>{c}</span>
              </label>
            ))}
          </div>
          {editing ? (
            <textarea value={selectedCaption} onChange={(e) => setSelectedCaption(e.target.value)}
              rows={3} className="mt-2 w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-pink-400" />
          ) : (
            <button onClick={() => setEditing(true)} className="mt-1 text-xs text-[#DC283C] hover:underline">Edit selected caption →</button>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-[#64748B]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={pubIG} onChange={(e) => setPubIG(e.target.checked)} className="accent-[#DC283C]" />
            <Instagram size={13} /> Instagram
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={pubFB} onChange={(e) => setPubFB(e.target.checked)} className="accent-[#DC283C]" />
            <Facebook size={13} /> Facebook
          </label>
        </div>

        {showSchedule && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2" data-testid={`doozyad-schedule-panel-${draft.draft_id}`}>
            <label className="block text-xs font-semibold text-[#64748B]">Publish at (your local time)</label>
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-violet-400"
              data-testid={`doozyad-schedule-input-${draft.draft_id}`}
            />
            <div className="flex gap-2">
              <button onClick={schedule} disabled={busy || !scheduleAt}
                className="flex-1 px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-md disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid={`doozyad-confirm-schedule-${draft.draft_id}`}>
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Calendar size={11} />} Confirm schedule
              </button>
              <button onClick={() => setShowSchedule(false)} disabled={busy}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-md hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100 flex-wrap">
          <button onClick={approve} disabled={busy || !selectedCaption.trim() || showSchedule}
            className="flex-1 min-w-[8rem] px-3 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid={`doozyad-approve-${draft.draft_id}`}>
            {busy && !showSchedule ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
            {canPublish ? "Approve & Publish now" : "Approve (creds pending)"}
          </button>
          <button onClick={() => setShowSchedule(!showSchedule)} disabled={busy || !selectedCaption.trim()}
            className="px-3 py-2 bg-violet-100 text-violet-700 text-xs font-semibold rounded-lg hover:bg-violet-200 disabled:opacity-50 flex items-center gap-1"
            data-testid={`doozyad-schedule-toggle-${draft.draft_id}`}>
            <Clock size={12} /> Schedule
          </button>
          <button onClick={skip} disabled={busy}
            className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 flex items-center gap-1"
            data-testid={`doozyad-skip-${draft.draft_id}`}>
            <X size={13} /> Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export const ScheduledCard = ({ draft, authAxios, onChange }) => {
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    if (!window.confirm("Cancel schedule and revert to pending?")) return;
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/drafts/${draft.draft_id}/cancel-schedule`);
      await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Cancel failed");
    }
    setBusy(false);
  };

  const sched = draft.scheduled_for ? new Date(draft.scheduled_for) : null;

  return (
    <div className="bg-white border border-violet-200 rounded-2xl overflow-hidden flex flex-col sm:flex-row" data-testid={`doozyad-scheduled-${draft.draft_id}`}>
      <div className="sm:w-48 shrink-0 bg-gray-100 aspect-square sm:aspect-auto relative">
        {draft.media_type === "video" ? (
          <video src={draft.public_image_url} className="w-full h-full object-cover" preload="metadata" />
        ) : (
          <img src={draft.public_image_url} alt={draft.theme_name} className="w-full h-full object-cover" loading="lazy" />
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 bg-violet-500/90 text-white text-[10px] font-semibold rounded-full uppercase tracking-wide flex items-center gap-1">
          <Calendar size={9} /> Scheduled
        </span>
      </div>
      <div className="flex-1 p-5 flex flex-col gap-2">
        <div className="text-xs text-[#DC283C] font-semibold uppercase tracking-wider">{draft.theme_name}</div>
        {sched && (
          <div className="text-sm font-bold text-violet-700 flex items-center gap-1.5">
            <Clock size={14} />
            {sched.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        )}
        <p className="text-xs text-[#1E1B4B] line-clamp-3">{draft.scheduled_caption || "(no caption)"}</p>
        <div className="flex items-center gap-3 text-xs text-[#64748B]">
          {draft.scheduled_publish_to_instagram && <span className="inline-flex items-center gap-1"><Instagram size={11} /> IG</span>}
          {draft.scheduled_publish_to_facebook && <span className="inline-flex items-center gap-1"><Facebook size={11} /> FB</span>}
        </div>
        <button
          onClick={cancel}
          disabled={busy}
          className="mt-auto self-start px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
          data-testid={`doozyad-cancel-schedule-${draft.draft_id}`}
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
          Cancel schedule
        </button>
      </div>
    </div>
  );
};

export const PublishedCard = ({ post, authAxios, onChange }) => {
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/refresh-insights/${post.draft_id}`);
      await onChange();
    } catch { /* ignore */ }
    setRefreshing(false);
  };
  const ins = post.insights || {};
  const canBoost = !!(post.ig_media_id || post.fb_post_id);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {post.media_type === "video" ? (
        <video src={post.public_image_url} className="w-full aspect-square object-cover" controls preload="metadata" />
      ) : (
        <img src={post.public_image_url} alt={post.theme_name} className="w-full aspect-square object-cover" loading="lazy" />
      )}
      <div className="p-4">
        <div className="text-xs text-[#DC283C] font-semibold uppercase tracking-wider mb-1">{post.theme_name}</div>
        <p className="text-xs text-[#1E1B4B] line-clamp-3">{post.selected_caption}</p>
        <div className="flex items-center gap-2 mt-3 text-xs text-[#64748B]">
          {post.ig_media_id && <span className="inline-flex items-center gap-1"><Instagram size={11} /> Live</span>}
          {post.fb_post_id && <span className="inline-flex items-center gap-1"><Facebook size={11} /> Live</span>}
          {post.published_via === "schedule" && <span className="inline-flex items-center gap-1 text-violet-600"><Calendar size={11} /> Scheduled</span>}
          <span className="ml-auto">{new Date(post.approved_at).toLocaleDateString("en-IN")}</span>
        </div>
        {post.tracking_urls?.instagram && (
          <details className="mt-2 text-[11px] text-[#64748B]" data-testid={`doozyad-tracking-${post.draft_id}`}>
            <summary className="cursor-pointer hover:text-[#DC283C] flex items-center gap-1"><LinkIcon size={11} /> UTM tracking URLs</summary>
            <div className="mt-1 space-y-1 pl-4 border-l-2 border-pink-100">
              <div className="break-all"><b>IG:</b> {post.tracking_urls.instagram}</div>
              <div className="break-all"><b>FB:</b> {post.tracking_urls.facebook}</div>
            </div>
          </details>
        )}
        {(ins.reach || ins.impressions || ins.engagement) ? (
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat label="Reach" value={ins.reach || 0} />
            <Stat label="Impr." value={ins.impressions || 0} />
            <Stat label="Eng." value={ins.engagement || 0} />
          </div>
        ) : (
          <button onClick={refresh} disabled={refreshing || !post.ig_media_id}
            className="mt-3 w-full px-3 py-1.5 bg-gray-50 border border-gray-200 text-xs text-[#64748B] rounded-lg hover:bg-gray-100 disabled:opacity-50 flex items-center justify-center gap-1">
            {refreshing ? <Loader2 size={11} className="animate-spin" /> : <BarChart3 size={11} />}
            {post.ig_media_id ? "Refresh insights" : "No IG media id (creds pending)"}
          </button>
        )}
        {canBoost && <BoostPanel post={post} authAxios={authAxios} />}
      </div>
    </div>
  );
};

export const BoostPanel = ({ post, authAxios }) => {
  const [open, setOpen] = useState(false);
  const [budget, setBudget] = useState(150);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [boost, setBoost] = useState(null);  // result of create or list
  const [error, setError] = useState("");

  // Try to find an existing boost for this draft when panel opens
  const loadExisting = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/boosts`);
      const found = (r.data?.boosts || []).find((b) => b.draft_id === post.draft_id);
      if (found) setBoost(found);
    } catch { /* ignore */ }
  }, [authAxios, post.draft_id]);

  useEffect(() => { if (open) loadExisting(); }, [open, loadExisting]);

  const createBoost = async () => {
    setBusy(true); setError("");
    try {
      const r = await authAxios.post(
        `${API_URL}/api/admin/doozyad/published/${post.draft_id}/boost`,
        { daily_budget_inr: Number(budget), duration_days: Number(days) },
        { timeout: 60000 },
      );
      setBoost(r.data);
      if (r.data.status === "creds_missing") {
        setError(`Setup needed: ${(r.data.missing || []).join(" · ")}`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Boost failed");
    }
    setBusy(false);
  };

  const flipStatus = async (action) => {
    if (!boost?.boost_id) return;
    setBusy(true); setError("");
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/boosts/${boost.boost_id}/${action}`);
      setBoost({ ...boost, status: r.data.status });
    } catch (e) {
      setError(e?.response?.data?.detail || `${action} failed`);
    }
    setBusy(false);
  };

  const refreshInsights = async () => {
    if (!boost?.boost_id) return;
    setBusy(true); setError("");
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/boosts/${boost.boost_id}/refresh-insights`);
      setBoost({ ...boost, insights: r.data.insights });
    } catch (e) {
      setError(e?.response?.data?.detail || "Refresh failed");
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 w-full px-3 py-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs font-bold rounded-lg hover:from-violet-600 hover:to-fuchsia-600 flex items-center justify-center gap-1.5"
        data-testid={`doozyad-boost-${post.draft_id}`}
      >
        <Rocket size={12} /> Boost this post
      </button>
    );
  }

  const ins = boost?.insights || {};
  const isCreated = boost && (boost.status === "paused" || boost.status === "active");

  return (
    <div className="mt-3 border-2 border-violet-200 rounded-xl p-3 bg-gradient-to-br from-violet-50 to-fuchsia-50" data-testid={`doozyad-boost-panel-${post.draft_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-violet-900 flex items-center gap-1">
          <Rocket size={12} /> Paid boost via Meta Ads
        </div>
        <button onClick={() => setOpen(false)} className="text-[#64748B] hover:text-[#1E1B4B]" data-testid={`doozyad-boost-close-${post.draft_id}`}>
          <X size={13} />
        </button>
      </div>

      {!isCreated && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="block text-[10px] font-semibold text-[#64748B] mb-0.5">Daily budget (₹)</label>
              <input
                type="number" min={50} max={50000} step={50}
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-violet-200 rounded outline-none focus:border-violet-500"
                data-testid={`doozyad-boost-budget-${post.draft_id}`}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-[#64748B] mb-0.5">Duration (days)</label>
              <input
                type="number" min={1} max={30}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-violet-200 rounded outline-none focus:border-violet-500"
                data-testid={`doozyad-boost-days-${post.draft_id}`}
              />
            </div>
          </div>
          <p className="text-[10px] text-[#64748B] mb-2">
            Total: ₹{(Number(budget) * Number(days)).toLocaleString("en-IN")} over {days} days · creates a PAUSED Meta campaign you must explicitly activate.
          </p>
          <button
            onClick={createBoost}
            disabled={busy || Number(budget) < 50}
            className="w-full px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid={`doozyad-boost-create-${post.draft_id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />}
            Create boost (paused for review)
          </button>
        </>
      )}

      {isCreated && (
        <>
          <div className="text-[11px] text-[#1E1B4B] mb-2">
            <span className="font-semibold">Status:</span>{" "}
            <span className={boost.status === "active" ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>
              {boost.status.toUpperCase()}
            </span>
            {" · "}₹{boost.daily_budget_inr}/day · {boost.duration_days} days
          </div>

          {(ins.spend_inr > 0 || ins.impressions > 0) && (
            <div className="grid grid-cols-3 gap-1.5 mb-2 text-center">
              <div className="bg-white rounded py-1">
                <div className="text-xs font-bold text-violet-700">₹{ins.spend_inr || 0}</div>
                <div className="text-[9px] text-[#64748B] uppercase">Spent</div>
              </div>
              <div className="bg-white rounded py-1">
                <div className="text-xs font-bold text-violet-700">{(ins.reach || 0).toLocaleString("en-IN")}</div>
                <div className="text-[9px] text-[#64748B] uppercase">Reach</div>
              </div>
              <div className="bg-white rounded py-1">
                <div className="text-xs font-bold text-violet-700">{ins.engagements || 0}</div>
                <div className="text-[9px] text-[#64748B] uppercase">Eng.</div>
              </div>
            </div>
          )}

          <div className="flex gap-1.5 flex-wrap">
            {boost.status === "paused" && (
              <button
                onClick={() => flipStatus("activate")}
                disabled={busy}
                className="flex-1 px-2 py-1 bg-emerald-600 text-white text-[11px] font-semibold rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid={`doozyad-boost-activate-${post.draft_id}`}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                Activate
              </button>
            )}
            {boost.status === "active" && (
              <button
                onClick={() => flipStatus("pause")}
                disabled={busy}
                className="flex-1 px-2 py-1 bg-amber-600 text-white text-[11px] font-semibold rounded hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid={`doozyad-boost-pause-${post.draft_id}`}
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
                Pause
              </button>
            )}
            <button
              onClick={refreshInsights}
              disabled={busy}
              className="px-2 py-1 bg-white border border-violet-200 text-violet-700 text-[11px] font-semibold rounded hover:bg-violet-50 disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid={`doozyad-boost-refresh-${post.draft_id}`}
            >
              <RefreshCw size={11} /> Insights
            </button>
            {boost.preview_url && (
              <a href={boost.preview_url} target="_blank" rel="noreferrer"
                className="px-2 py-1 bg-white border border-violet-200 text-violet-700 text-[11px] font-semibold rounded hover:bg-violet-50 flex items-center gap-1">
                <ExternalLink size={11} /> Ads Mgr
              </a>
            )}
          </div>
        </>
      )}

      {error && (
        <div className="mt-2 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid={`doozyad-boost-error-${post.draft_id}`}>
          {error}
        </div>
      )}
    </div>
  );
};

export const Stat = ({ label, value }) => (
  <div className="bg-pink-50 rounded-lg py-1.5">
    <div className="text-sm font-bold text-[#DC283C]">{value.toLocaleString("en-IN")}</div>
    <div className="text-[10px] text-[#64748B] uppercase tracking-wide">{label}</div>
  </div>
);
